const SKALD = { mob: 'snow-skald', biome: 'snow' };
const SEER = { mob: 'snow-seer', biome: 'snow' };
const SCOUT = { mob: 'snow-scout', biome: 'snow' };
const WARDEN = { mob: 'snow-warden', biome: 'snow' };
/** Ser Givrecoeur, at the Company's quarter in Havrebourg (ADR 0067). */
const GIVRECOEUR = { mob: 'tutor', biome: 'meadow' };
/**
 * What the Frost Giant's fall set loose (ADR 0067, `docs/LORE.md`). The
 * Giant was the last lock of the seal, sitting on the Throne above
 * Shefira's sleep: Runa's forgotten verse and Eira's visions only make sense
 * once he is dead. The ice cracks, La Brèche opens, and its turn-in plays
 * the dragon's cinematic (`snow-breach` → `shefira-breach`). Ulfar then
 * swears a last oath and joins the Company in Havrebourg; the battle at
 * Givrecime is for later. The main chain runs from the Giant through
 * `snow-farewell` and these quests to Ulfar's recruitment; `snow-homecoming`
 * (`snow-quests.ts`) waits for the Brèche too.
 */
export const SNOW_DRAGON_QUESTS = [
    {
        id: 'snow-last-verse',
        name: 'Le dernier couplet',
        biome: 'snow',
        level: 20,
        requires: ['snow-farewell'],
        giver: SKALD,
        turnIn: SEER,
        offer: 'Ce couplet ne me sort plus de la tête : « Que le froid siège au-dessus du feu, et le feu dormira. » Ma mère le chantait tout bas, je croyais que c’était une prière contre l’hiver. Va voir Eira, qu’elle me dise que je me trompe. Et attention, depuis la chute du Géant, les loups sont devenus fous.',
        summary: 'Abattre les loups devenus fous et rejoindre la Voyante Eira aux Cristaux du Givre.',
        pending: 'Mes cristaux ne chantent plus. Ça ne leur était jamais arrivé !',
        complete: 'Runa t’a chanté ce couplet ? Alors c’est vrai… Je l’ai vue cent fois dans la glace sans comprendre. Ce n’était pas le Géant qui bougeait sous le Trône. C’était elle : Shefira, le dragon ! Le Géant, c’était le froid assis au-dessus du feu, le dernier cadenas. Et c’est nous qui t’avons envoyé le battre. Oups.',
        objectives: [
            { kind: 'kill', label: 'Loups devenus fous abattus', mobs: ['ice-wolf'], count: 20 },
            { kind: 'reach', label: 'Atteindre les Cristaux du Givre', landmark: 'Cristaux du Givre' },
        ],
        reward: { xp: 900, gold: 180 },
    },
    {
        id: 'snow-meltwater',
        name: 'L’eau sous la neige',
        biome: 'snow',
        level: 20,
        requires: ['snow-last-verse'],
        giver: SEER,
        turnIn: WARDEN,
        offer: 'Ne t’en veux pas, personne ne savait ! Il faut savoir où en est la glace. Torvald voit toute la vallée depuis la Crête, demande-lui ce qu’il a vu. Puis va voir Ulfar au Trône. Attention aux harpies, elles fuient les sommets et attaquent tout ce qui bouge.',
        summary: 'Interroger l’Éclaireur Torvald, abattre les harpies qui fuient les cimes, puis rejoindre Ulfar au Trône.',
        pending: 'Tu as vu Torvald ? Moi, je n’ai pas besoin de lui pour savoir. Je l’entends, la nuit.',
        complete: 'Une fumée sur un glacier, en plein hiver ? Torvald n’a pas rêvé. La nuit, sous mes pieds, quelque chose respire, et la roche est tiède. J’ai gardé cette porte pendant vingt ans pour bloquer le Géant… et je gardais la mauvaise porte.',
        objectives: [
            {
                kind: 'talk',
                label: 'Interroger l’Éclaireur Torvald',
                npc: SCOUT,
                line: 'Toi aussi, tu l’as senti ? Hier soir, de la fumée blanche est sortie d’un glacier, au pied des sommets. Et ce matin, de l’eau coulait sous la neige. En plein hiver ! Je n’ai jamais eu peur sur ma crête, mais là, je n’ose plus monter. Et je n’ai même plus envie de boire, c’est te dire.',
            },
            { kind: 'kill', label: 'Harpies des neiges abattues', mobs: ['snow-harpy'], count: 20 },
        ],
        reward: { xp: 1000, gold: 200, items: [{ templateId: 'snow-potion', quantity: 2 }] },
    },
    {
        id: 'snow-breach',
        name: 'La Brèche',
        biome: 'snow',
        level: 20,
        requires: ['snow-meltwater'],
        giver: WARDEN,
        turnIn: WARDEN,
        offer: 'Le glacier qui fume, c’est là qu’on a endormi le dragon, il y a sept cents ans. Monte jusqu’à la Brèche et regarde. Les guerriers d’Eirik sont encore là-bas, transformés en fantômes de glace. Libère-les, puis reviens me dire ce que tu as vu.',
        summary: 'Atteindre la Brèche, libérer les revenants qui la gardent, puis revenir auprès du Vieil Ulfar.',
        pending: 'La Brèche est au pied des sommets. Suis l’eau qui coule sous la neige, elle vient de là.',
        complete: 'Tu l’as vue ? Là-haut, regarde ! Rouge comme le feu, avec la glace qui fond sur son dos, et la chaîne cassée autour du cou ! Elle nous a vus… et elle nous a laissés tranquilles. Elle s’envole vers les sommets pour reprendre des forces. Et quand ce sera fait, elle reviendra.',
        objectives: [
            { kind: 'reach', label: 'Atteindre la Brèche', landmark: 'La Brèche' },
            {
                kind: 'kill',
                label: 'Revenants de la Brèche libérés',
                mobs: ['frost-revenant'],
                count: 15,
            },
        ],
        reward: { xp: 1500, gold: 300 },
    },
    {
        id: 'snow-recruit',
        name: 'Le dernier serment',
        biome: 'snow',
        level: 20,
        requires: ['snow-breach'],
        giver: WARDEN,
        turnIn: GIVRECOEUR,
        offer: 'Ces couleurs… c’est la Compagnie de Givrecoeur ! On était petits ensemble, ici. Il a vu la lumière sous la glace, et nous, on ne l’a pas cru. On l’a laissé partir tout seul. Ma promesse à Eirik est tenue, alors j’en fais une nouvelle : quand le dragon reviendra, je serai là. Passe voir Runa, qu’elle l’ajoute à son histoire. Puis dis à Givrecoeur que le vieil Ulfar arrive.',
        summary: 'Porter le serment d’Ulfar à la Skalde Runa, puis rejoindre Ser Givrecoeur à Havrebourg.',
        pending: 'Tu reviens de Givrecime ? Tu as de la vraie neige dans les cheveux, ça me rappelle chez moi. Raconte !',
        complete: 'Ulfar est vivant ?! On faisait des batailles de boules de neige tous les hivers, lui et moi ! Alors le dragon est libre… J’aurais tellement préféré me tromper. Regarde notre quartier, recrue : une bannière, une table, et tous ceux que tu as rassemblés. Ce n’est pas grand-chose contre un dragon, mais c’est une sacrée équipe. Quand elle aura repris ses forces, on ira l’affronter à Givrecime.',
        objectives: [
            {
                kind: 'talk',
                label: 'Porter le serment d’Ulfar à Runa',
                npc: SKALD,
                line: 'Ulfar fait une nouvelle promesse ? Il aura son couplet ! J’allais chanter ta victoire, et finalement, je vais chanter notre erreur, pour qu’on ne l’oublie jamais. Et je ne sais toujours pas comment l’histoire se termine.',
            },
            { kind: 'reach', label: 'Retourner à Havrebourg', landmark: 'Havrebourg' },
        ],
        reward: { xp: 800, gold: 160 },
    },
];
//# sourceMappingURL=snow-dragon.js.map